import { useState } from "react";
import { Download } from "lucide-react";
import axiosInstance from "../../API/interceptor";

// ── Date formatter ─────────────────────────────────────────────
function safeFormatDate(val) {
  if (!val) return "";
  try {
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dd   = String(d.getDate()).padStart(2, "0");
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch {
    return String(val);
  }
}


// ── CSV cell escape ────────────────────────────────────────────
const csvCell = val => `"${String(val ?? "").replace(/"/g, '""')}"`;

// ── All columns config — same as HTML ─────────────────────────
const ALL_COLUMNS = [
  { index: 1,  label: "Task ID" },
  { index: 2,  label: "Task" },
  { index: 3,  label: "Priority" },
  { index: 4,  label: "Task Status" },
  { index: 5,  label: "Workspace" },
  { index: 6,  label: "Functional Categories" },
  { index: 7,  label: "Accountable" },
  { index: 8,  label: "Responsible" },
  { index: 9,  label: "Consulted" },
  { index: 10, label: "Informed" },
  { index: 11, label: "Created At" },
  { index: 12, label: "Start Date" },
  { index: 13, label: "Completed At" },
  { index: 14, label: "Completed By" },
  { index: 15, label: "Deadline" },
];

// ── Row → column value — same as HTML switch ──────────────────
function getColValue(row, index) {
  switch (index) {
    case 1:  return row.unique_seq_id || "";
    case 2:  return row.task_type || "";
    case 3:  return row.priority || "";
    case 4:  return row.task_status || "";
    case 5:  return row.product_name || row.product_code || "";
    case 6:  return Array.isArray(row.functional_categories)
               ? row.functional_categories.join("; ")
               : (row.functional_categories || "");
    case 7:  return row.task_created_by || "";
    case 8:  return Array.isArray(row.assigned_to_name)
               ? row.assigned_to_name.join("; ")
               : (row.assigned_to_name || "");
    case 9:  return Array.isArray(row.observer_to_name)
               ? row.observer_to_name.join("; ")
               : (row.observer_to_name || "");
    case 10: return Array.isArray(row.informer_to_name)
               ? row.informer_to_name.join("; ")
               : (row.informer_to_name || "");
    case 11: return safeFormatDate(row.task_created_at);
    case 12: return safeFormatDate(row.start_date);
    case 13: return safeFormatDate(row.completed_at);
    case 14: return row.completed_by || "";
    case 15: return safeFormatDate(row.completion_sla);
    default: return "";
  }
}

// ── Build CSV string from tasks ────────────────────────────────
function buildCsv(tasks, cols) {
  const header = cols.map(c => csvCell(c.label)).join(",");
  const rows   = tasks.map(row =>
    cols.map(c => csvCell(getColValue(row, c.index))).join(",")
  );
  return [header, ...rows].join("\n");
}

// ── Trigger browser download ───────────────────────────────────
function triggerDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.style.display = "none";
  a.href          = url;
  a.download      = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ── Build FormData from filters ────────────────────────────────
function buildFormData(filters) {
  const fd = new FormData();

  // product_code — numeric codes array
  const codes = filters.product_code || [];
  if (codes.length > 0) {
    codes.forEach(c => fd.append("product_code[]", c));
  } else {
    fd.append("product_code", "0");
  }

  fd.append("task_type",    filters.task_type    || "");
  fd.append("search_value", filters.search_value || "");

  const appendArr = (key, arr) => {
    if (arr && arr.length > 0) arr.forEach(v => fd.append(`${key}[]`, v));
  };

  appendArr("assigned_to",           filters.assigned_to           || []);
  appendArr("priority",              filters.priority              || []);
  appendArr("created_by",            filters.created_by            || []);
  appendArr("informer",              filters.informer              || []);
  appendArr("observer",              filters.observer              || []);
  appendArr("status",                filters.status                || []);
  appendArr("functional_categories", filters.functional_categories || []);

  if (filters.created_at)    fd.append("created_at",    filters.created_at);
  if (filters.task_deadline) fd.append("task_deadline",  filters.task_deadline);
  if (filters.start_date)    fd.append("start_date",    filters.start_date);

  return fd;
}

/* ══ EXPORT MODAL ══════════════════════════════════════════════ */
function ExportModal({ onClose, onExport, hasFilters, totalRecords }) {
  const [exportType, setExportType] = useState("all");

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/45"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-emerald-600 to-emerald-700">
            <Download size={16} color="#fff" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800 m-0">Export Tasks</p>
            <p className="text-xs text-slate-400 m-0">
              {totalRecords} records {hasFilters ? "(filtered)" : ""}
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="px-5 py-4 flex flex-col gap-3">

          {/* All Columns */}
          <label
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
              exportType === "all"
                ? "border-emerald-400 bg-emerald-50"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <input
              type="radio"
              name="exportType"
              value="all"
              checked={exportType === "all"}
              onChange={() => setExportType("all")}
              className="mt-0.5 accent-emerald-600"
            />
            <div>
              <p className="text-xs font-bold text-slate-700 m-0">All Columns</p>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Full export — backend CSV direct download
              </p>
            </div>
          </label>

          {/* Visible Columns */}
          <label
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
              exportType === "visible"
                ? "border-emerald-400 bg-emerald-50"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <input
              type="radio"
              name="exportType"
              value="visible"
              checked={exportType === "visible"}
              onChange={() => setExportType("visible")}
              className="mt-0.5 accent-emerald-600"
            />
            <div>
              <p className="text-xs font-bold text-slate-700 m-0">Visible Columns Only</p>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Only columns visible in the table will be included in the CSV
              </p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50 flex gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-colors bg-white border-[1.5px] border-slate-200 text-slate-500"
          >
            Cancel
          </button>
          <button
            onClick={() => onExport(exportType)}
            className="flex-1 py-2.5 rounded-xl border-none text-white text-xs font-bold cursor-pointer bg-gradient-to-br from-emerald-600 to-emerald-700 shadow-[0_3px_12px_rgba(5,150,105,.35)]"
          >
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ TOAST ═════════════════════════════════════════════════════ */
function Toast({ msg, type, onClose }) {
  return (
    <div
  className={`fixed bottom-6 right-6 z-[99999] flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-xl text-sm font-medium text-white min-w-[260px] max-w-[380px] animate-[slideUp_.25s_ease] ${
    type === "success"
      ? "bg-gradient-to-br from-emerald-600 to-emerald-700"
      : "bg-gradient-to-br from-red-600 to-red-700"
  }`}
>
      <span className="text-base">{type === "success" ? "✅" : "❌"}</span>
      <span className="flex-1 text-xs">{msg}</span>
      <button
        onClick={onClose}
        className="bg-transparent border-none text-white opacity-70 hover:opacity-100 cursor-pointer text-base leading-none"
      >×</button>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
}

/* ══ MAIN EXPORT BUTTON ════════════════════════════════════════ */
export default function ExportButton({ filters, totalRecords, hasFilters }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast,     setToast]     = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── All Columns — backend CSV blob via axiosInstance ─────────
  const exportAll = async () => {
    setExporting(true);
    setModalOpen(false);
    try {
      const fd = buildFormData(filters);

      const response = await axiosInstance.post(
        "/task_assignment/export_tasks",
        fd,
        {
          responseType: "blob",  // CSV binary
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Filename from Content-Disposition header
      const disposition = response.headers["content-disposition"] || "";
      const match       = disposition.match(/filename=([^;]+)/);
      const fileName    = match
        ? match[1].replace(/['"]/g, "").trim()
        : "task_export.csv";

      triggerDownload(response.data, fileName);
      showToast(`Export successful! Downloaded: ${fileName}`, "success");
    } catch (err) {
      // Parse blob error response
      let msg = "Export failed";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch { /* ignore */ }
      } else {
        msg = err.response?.data?.error || err.message || msg;
      }
      showToast(msg, "error");
    } finally {
      setExporting(false);
    }
  };

  // ── Visible Columns — JSON response, frontend CSV build ──────
  const exportVisible = async () => {
    setExporting(true);
    setModalOpen(false);
    try {
      const fd = buildFormData(filters);
      fd.append("export_format", "json");

      const response = await axiosInstance.post(
        "/task_assignment/export_tasks",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const tasks = response.data?.tasks || [];
      if (!tasks.length) {
        showToast("No data to export", "error");
        return;
      }

      // BOM + CSV build
      const csv      = buildCsv(tasks, ALL_COLUMNS);
      const blob     = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const fileName = `task_export_${new Date().toISOString().slice(0, 10)}.csv`;
      triggerDownload(blob, fileName);
      showToast(`Export successful! ${tasks.length} tasks downloaded.`, "success");
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Export failed";
      showToast(msg, "error");
    } finally {
      setExporting(false);
    }
  };

  // ── Handle export type selection ─────────────────────────────
  const handleExport = (type) => {
    // 1000+ records — extra confirm (same as HTML)
    if (type === "all" && totalRecords > 1000) {
      if (!window.confirm(
        `${totalRecords} records will be exported. This may take a few moments. Continue?`
      )) return;
    }
    if (type === "all") exportAll();
    else exportVisible();
  };

  return (
    <>
      {/* ── Export Button ── */}
      <button
        onClick={() => !exporting && setModalOpen(true)}
        disabled={exporting}
        className={`flex items-center gap-1.5 border rounded-lg px-2.5 md:px-3.5 py-2 text-xs md:text-sm font-semibold cursor-pointer transition-all flex-shrink-0 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
          hasFilters
            ? "bg-[#FFF7ED] border-[#FDE68A] text-[#D97706]"
            : "bg-[#ECFDF5] border-[#A7F3D0] text-[#059669]"
        }`}
      >
        <Download
          size={13}
          className={exporting ? "animate-bounce" : ""}
        />
        <span className="hidden sm:inline">
          {exporting
            ? "Exporting..."
            : hasFilters
              ? "Export Filtered"
              : "Export All"}
        </span>
      </button>

      {/* ── Modal ── */}
      {modalOpen && (
        <ExportModal
          onClose={() => setModalOpen(false)}
          onExport={handleExport}
          hasFilters={hasFilters}
          totalRecords={totalRecords}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <Toast
          msg={toast.msg}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}